import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import usePostData from "../../../../repo/post";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';

const ExcelToJson = () => {
  const [apiResponse, setApiResponse] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredData, setFilteredData] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogData, setDialogData] = useState([]);
  const { postData } = usePostData();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const isCSV = file.name.toLowerCase().endsWith('.csv');
        const data = e.target.result;

        const workbook = XLSX.read(
          data,
          { type: isCSV ? 'string' : 'binary', csv: isCSV }
        );

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const newData = jsonData.map((row) => ({
          data: row['Entregue em'],
          codigo: row['Código de Barras'],
        }));

        setFilteredData(newData);
        await fetchData(newData);
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
      }
    };

    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file, 'UTF-8');
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const fetchData = async (data) => {
    try {
      setIsLoading(true);
      const response = await postData(`api/codigos/codigosIn`, data);
      setApiResponse(response.data ? response.data : response);
    } catch (error) {
      console.error('Erro ao enviar os dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const showDialog = (title, items) => {
    setDialogTitle(title);
    setDialogData(items || []);
    setDialogVisible(true);
  };

  const exportToExcel = () => {
    const tableData = apiResponse.map(item => ({
      nome: item.entregador?.nome ?? 'N/A',
      total: item.total ?? 0,
      prazo: item.prazo?.length ?? 0,
      atrasado: item.atrasado?.length ?? 0,
    }));
    if (tableData.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório");
    XLSX.utils.sheet_add_aoa(worksheet, [["Entregador", "Total", "Prazo", "Atrasado"]], { origin: "A1" });
    XLSX.writeFile(workbook, "relatorio_entregas.xlsx");
  };

  // Prepare table rows
  const tableData = apiResponse.map(item => ({
    nome: item.entregador?.nome ?? 'N/A',
    total: item.total ?? 0,
    prazo: item.prazo || [],
    atrasado: item.atrasado || []
  }));

  // Cell templates
  const prazoBody = (rowData) => (
    <Button
      label={rowData.prazo.length}
      className="p-button-text p-button-sm"
      onClick={() => showDialog('Entregas no Prazo', rowData.prazo)}
      disabled={rowData.prazo.length === 0}
    />
  );

  const atrasadoBody = (rowData) => (
    <Button
      label={rowData.atrasado.length}
      className="p-button-text p-button-sm"
      onClick={() => showDialog('Entregas Atrasadas', rowData.atrasado)}
      disabled={rowData.atrasado.length === 0}
    />
  );

  return (
    <div className="relative flex flex-col w-full mb-6 shadow-lg rounded-lg bg-white p-6">
      {isLoading && (
        <div className="fixed inset-0 bg-opacity-40 flex items-center justify-center z-50">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
        </div>
      )}

      {/* Upload Section */}
      <div className="flex flex-col gap-4 bg-gray-50 rounded-lg p-4 mb-4 shadow-sm border">
        <label className="text-gray-700 font-semibold">Upload de Arquivo</label>
        <div className="flex gap-4 items-center">
          <Button icon="pi pi-upload" label="Escolher Arquivo" onClick={() => document.getElementById('file-input').click()} className="p-button-outlined p-button-info" />
          <input id="file-input" type="file" accept=".xlsx, .csv" onChange={handleFileUpload} className="hidden" />
          <small className="text-gray-500">Formatos suportados: .xlsx, .csv</small>
          <Button label="Pesquisar" icon="pi pi-search" onClick={() => filteredData && fetchData(filteredData)} disabled={!filteredData || isLoading} className="p-button-info bg-blue-500 text-white m-4 px-3 py-1 rounded-md shadow hover:bg-lightBlue-600 font-bold" tooltip="Clique para executar a pesquisa" tooltipOptions={{ position: 'top' }} />
        </div>
      </div>

      {/* Export and Table */}
      <div className="mt-4">
        <Button label="Exportar XLSX" icon="pi pi-file-excel" onClick={exportToExcel} disabled={tableData.length === 0} className="p-button-success bg-blue-500 text-white m-4 px-3 py-1 rounded-md shadow hover:bg-lightBlue-600 font-bold" tooltip="Exportar dados para Excel" tooltipOptions={{ position: 'top' }} />
        <DataTable value={tableData} paginator rows={30} stripedRows rowsPerPageOptions={[5, 10, 30, 50, 100, 300]} emptyMessage="Nenhum resultado encontrado." loading={isLoading}>
          <Column field="nome" header="Entregador" style={{ minWidth: "12rem", fontSize: "13px" }} />
          <Column field="total" header="Total" sortable style={{ minWidth: "12rem", fontSize: "13px" }} />
          <Column header="Prazo" body={prazoBody} sortable style={{ minWidth: "12rem", fontSize: "13px" }} />
          <Column header="Atrasado" body={atrasadoBody} sortable style={{ minWidth: "12rem", fontSize: "13px" }} />
        </DataTable>
      </div>

      {/* Dialog for details */}
      <Dialog header={dialogTitle} visible={dialogVisible} style={{ width: '40vw' }} onHide={() => setDialogVisible(false)}>
        {dialogData.length > 0 ? (
          <ul className="list-disc list-inside">
            {dialogData.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        ) : (
          <p>Nenhum item para exibir.</p>
        )}
      </Dialog>
    </div>
  );
};

export default ExcelToJson;